<template>
  <div class="flex flex-col rounded-md bg-neutral-secondary h-full pb-1">
    <NTabs v-model="tab" variant="underline" class="w-full mt-1">
      <NTab :title="t('modules.watchlist.title')">
        <template #prepend>
          <NegarStarLightIcon />
        </template>

        <div class="mx-2">
          <NListbox
            ref="listBoxRef"
            v-model="selectedWatchlistModel"
            input-size="small"
          >
            <NButton
              v-gid="{
                module: 'watchlist',
                screen: 'home',
                section: 'watchlistDropDown',
                component: 'createWatchlistButton',
              }"
              variant="outline"
              color="green"
              class="w-full border-none flex items-center justify-center"
              @click="handleOpenCreateWatchlistDialog"
            >
              <NegarPlusOutlineIcon size="16" class="text-brand-primary ml-1" />

              <NText variant="caption1" weight="medium">
                {{ t('modules.watchlist.create_title') }}
              </NText>
            </NButton>

            <NListboxOption
              v-if="symbols.length"
              :value="PORTFOLIO_INDEX"
              :label="t('modules.portfolio.title')"
            >
              <NText weight="medium" variant="caption1">
                {{ t('modules.portfolio.title') }}
              </NText>
            </NListboxOption>

            <WatchlistItem
              v-for="(watchlistData, index) of watchList"
              :key="`${watchlistData.title}${index}`"
              v-gid="{
                module: 'watchlist',
                screen: 'home',
                section: 'watchlistDropDown',
                component: 'watchlistItem',
              }"
              :value="index + (PORTFOLIO_INDEX + 1)"
              :watchlist="watchlistData"
              @after-delete="handleAfterDeleteWatchlist"
              @after-update="handleAfterUpdateWatchlist"
              @close="listBoxRef?.handleClose"
            />
          </NListbox>
        </div>

        <ComponentState :fetch-status="symbolsStatus">
          <template #loading>
            <div class="mt-3 mx-2">
              <TableSkeleton :row-number="10" :column-number="3" />
            </div>
          </template>

          <template #error>
            <Error class="p-4" @retry="retry" />
          </template>

          <SymbolsTable
            v-if="isPortfolioSelected ? symbols.length : watchListItems.length"
            :data="isPortfolioSelected ? symbols : watchListItems"
            :is-portfolio-selected="isPortfolioSelected"
            :selected-watchlist-id="selectedWatchlistId"
            :offers-isin="offersIsin"
            :offers="initialPublicOffers"
            :selected-watchlist-title="selectedWatchlistModel?.label"
            @handle-after-create-watchlist="handleAfterCreateWatchlist"
          />

          <div
            v-else
            class="bg-neutral-primary overflow-y-auto m-2 h-full flex items-center justify-center"
          >
            <EmptyWatchlistState
              :is-portfolio-selected="isPortfolioSelected"
              :portfolio-symbols="symbols"
              :watch-list="watchList"
              @handle-after-create-watchlist="handleAfterCreateWatchlist"
            />
          </div>
        </ComponentState>
      </NTab>

      <NTab :title="t('symbol.industry')">
        <template #prepend>
          <NegarIndustryIcon />
        </template>

        <div class="px-2">
          <NListbox
            ref="industryListBoxRef"
            v-model="selectedIndustryModel"
            v-gid="{
              module: 'optionChain',
              screen: 'home',
              section: 'optionChainListBox',
              component: 'optionChainListBox',
            }"
            input-size="small"
          >
            <NListboxOption :disabled="true" class="cursor-text">
              <NText weight="medium" variant="caption1" class="flex">
                {{ GROUPING_INDUSTRY_SYMBOLS.tradableFunds.name }}
              </NText>
            </NListboxOption>

            <template
              v-for="item in GROUPING_INDUSTRY_SYMBOLS.tradableFunds.symbols"
              :key="item"
            >
              <NListboxOption
                :value="item.name"
                @click="handleSymbolGroupClick(item.filters, false)"
              >
                <div class="w-full flex items-center">
                  <component :is="item.icon" />

                  <NText weight="medium" variant="caption1" class="ms-2">
                    {{ item.name }}
                  </NText>
                </div>
              </NListboxOption>
            </template>

            <NListboxOption
              :label="t('search.appliedIndustries')"
              :disabled="true"
              class="cursor-text"
            >
              <NText weight="medium" variant="caption1" class="flex">
                {{ t('search.appliedIndustries') }}
              </NText>
            </NListboxOption>

            <template v-for="item in classificationGroupSymbols" :key="item">
              <NListboxOption
                :value="item.shortName"
                @click="handleSymbolGroupClick(item, true)"
              >
                <div class="w-full flex items-center">
                  <component
                    :is="
                      GROUPING_INDUSTRY_SYMBOLS_SERVICE[Number(item.code)].icon
                    "
                  />

                  <NText weight="medium" variant="caption1" class="ms-2">
                    {{ item.shortName }}
                  </NText>
                </div>
              </NListboxOption>
            </template>

            <NListboxOption
              :label="GROUPING_INDUSTRY_SYMBOLS.functionalCategories.name"
              :disabled="true"
              class="cursor-text"
            >
              <NText weight="medium" variant="caption1">
                {{ GROUPING_INDUSTRY_SYMBOLS.functionalCategories.name }}
              </NText>
            </NListboxOption>

            <template
              v-for="item in GROUPING_INDUSTRY_SYMBOLS.functionalCategories
                .symbols"
              :key="item"
            >
              <NListboxOption
                :value="item.name"
                @click="handleSymbolGroupClick(item.filters, false)"
              >
                <div class="w-full flex items-center">
                  <component :is="item.icon" />

                  <NText weight="medium" variant="caption1" class="ms-2">
                    {{ item.name }}
                  </NText>
                </div>
              </NListboxOption>
            </template>
          </NListbox>
        </div>

        <ComponentState :fetch-status="classificationSymbolsItemsStatus">
          <template #loading>
            <div class="mt-3 mx-2">
              <TableSkeleton :row-number="10" :column-number="3" />
            </div>
          </template>

          <template #error>
            <Error class="p-4" @retry="getClassificationSymbolsItemList" />
          </template>

          <ClassificationSymbolsTable
            :data="classificationSymbolsItems"
            :offers-isin="offersIsin"
            :sort-by="tableSortBy"
            :offers="initialPublicOffers"
          />
        </ComponentState>
      </NTab>
    </NTabs>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  NegarPlusOutlineIcon,
  NegarIndustryIcon,
  NegarStarLightIcon,
} from '@negar/icons';
import {
  useGetWatchlist,
  useLiveWatchlistItems,
  WATCHLIST_QUERY_KEYS,
} from '@core/watchlist';
import { useLivePortfolioQuery } from '@core/portfolio';
import {
  useGetClassificationSymbols,
  useLiveClassificationSymbolsItemsQuery,
} from '@core/symbol';
import { useLiveInitialPublicOffers } from '@core/ipo';
import { sleep } from '@libs/utils';
import { queryStatus, useQueryClient } from '@libs/use';
import { useFeatureFlag } from '@libs/feature-flag';

import {
  GROUPING_INDUSTRY_SYMBOLS,
  GROUPING_INDUSTRY_SYMBOLS_SERVICE,
} from '@/modules/symbol';
import { useWatchlistDialogs, EmptyWatchlistState } from '@/modules/watchlist';

import Error from '@/components/Error.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import ComponentState from '@/components/ComponentState.vue';

import SymbolsTable from './SymbolsTable.vue';
import ClassificationSymbolsTable from './ClassificationSymbolsTable.vue';
import WatchlistItem from './WatchlistItem.vue';

type WatchlistProps = {
  isExpandedNews: boolean;
};

defineProps<WatchlistProps>();

const PORTFOLIO_INDEX = 0;
const PORTFOLIO_OFFSET = PORTFOLIO_INDEX + 1;
const SYMBOL_MODEL_INITIAL_STATE = {
  sectorCodes: '',
  name: '',
  types: '305',
  subSectorCodes: '6811',
  instrumentGroupIds: '',
} as const;

const DEFAULT_TABLE_SORT = {
  prop: 'pricePercentage',
  order: 'descending',
} as const;

const symbolModelValue = reactive({ ...SYMBOL_MODEL_INITIAL_STATE });

// todo: expose table type from negar;
const tableSortBy = ref(DEFAULT_TABLE_SORT);
const selectedWatchlistModel = ref();
const selectedIndustryModel = ref({
  label: GROUPING_INDUSTRY_SYMBOLS.tradableFunds.symbols[1].name,
  value: GROUPING_INDUSTRY_SYMBOLS.tradableFunds.symbols[1].name,
});

const listBoxRef = ref();
const industryListBoxRef = ref();

const INDUSTRY_TAB_VALUE = 1;
const tab = ref(0);

const isOption = useFeatureFlag('option');

const isPortfolioSelected = computed(
  () => selectedWatchlistModel.value?.value === PORTFOLIO_INDEX
);
const selectedWatchlist = computed<string>(() =>
  String(selectedWatchlistModel.value?.value - PORTFOLIO_OFFSET)
);
const selectedWatchlistId = computed<string>(() => {
  return isPortfolioSelected.value || !watchList.value
    ? null
    : watchList.value[selectedWatchlist.value]?.id;
});
const classificationGroupSymbols = computed(() => {
  return sortClassificationSymbols.value.filter(
    (item) => item.popular === true
  );
});
const isLoadingOrRevalidatingPortfolio = computed(
  () => isLoadingSymbols.value || isRevalidatingSymbols.value
);
const isLoadingOrRevalidatingWatchlist = computed(
  () => isLoadingWatchlist.value || isRevalidatingWatchlist.value
);

const queryClient = useQueryClient();
const { t } = useI18n();
const route = useRoute();

const { openCreateWatchlistDialog } = useWatchlistDialogs();

const {
  symbols,
  isRevalidatingSymbols,
  isLoadingSymbols,
  isSymbolLoaded,
  isErrorLoadingPortfolio,
  getPortfolio,
} = useLivePortfolioQuery({
  listenToPreOpeningPriceChannel: true,
});

const { offersIsin, initialPublicOffers } = useLiveInitialPublicOffers();

const {
  watchList,
  getWatchlist,
  isLoadingWatchlist,
  isRevalidatingWatchlist,
  watchlistStatus,
} = useGetWatchlist();

const { watchListItems, watchlistItemsStatus, getWatchlistItems } =
  useLiveWatchlistItems(selectedWatchlistId, {
    listen: true,
    immediate: computed(() => !!selectedWatchlistId.value),
  });

const { sortClassificationSymbols } = useGetClassificationSymbols({
  immediate: computed(() => tab.value === INDUSTRY_TAB_VALUE),
});

const {
  classificationSymbolsItems,
  classificationSymbolsItemsStatus,
  getClassificationSymbolsItemList,
} = useLiveClassificationSymbolsItemsQuery(symbolModelValue, {
  immediate: computed(() => tab.value === INDUSTRY_TAB_VALUE),
});

const resetSymbolModelValue = () => {
  Object.assign(symbolModelValue, SYMBOL_MODEL_INITIAL_STATE);
};

const selectPortfolio = () => {
  selectedWatchlistModel.value = {
    value: PORTFOLIO_INDEX,
    label: t('modules.portfolio.title'),
  };
};

const handleOpenCreateWatchlistDialog = () => {
  listBoxRef.value.handleClose();

  openCreateWatchlistDialog({ handleAfterCreateWatchlist });
};

const handleAfterUpdateWatchlist = async (updatedValue: number) => {
  queryClient.invalidateQueries(WATCHLIST_QUERY_KEYS.watchlistOverview);

  await getWatchlist();

  await sleep();

  if (updatedValue === selectedWatchlistModel.value.value) {
    const selectedWatchlist = watchList.value[updatedValue - PORTFOLIO_OFFSET];

    selectedWatchlistModel.value = {
      value: updatedValue,
      label: selectedWatchlist.title,
    };
  }
};

const handleAfterDeleteWatchlist = async (deletedValue: number) => {
  if (deletedValue === selectedWatchlistModel.value.value) {
    selectPortfolio();
  }

  queryClient.invalidateQueries(WATCHLIST_QUERY_KEYS.watchlistOverview);

  await getWatchlist();
};

const handleAfterCreateWatchlist = async () => {
  queryClient.invalidateQueries(WATCHLIST_QUERY_KEYS.watchlistOverview);

  await getWatchlist();

  await sleep();

  const lastWatchlistIndex = watchList.value.length - 1;
  const lastWatchlist = watchList.value[lastWatchlistIndex];

  selectedWatchlistModel.value = {
    value: lastWatchlistIndex + PORTFOLIO_OFFSET,
    label: lastWatchlist.title,
  };
};

// toDo: type of item
const handleSymbolGroupClick = (item, symbolFromServer: boolean, sortBy?) => {
  industryListBoxRef.value?.handleClose();

  if (sortBy) {
    tableSortBy.value = sortBy;
  } else {
    tableSortBy.value = DEFAULT_TABLE_SORT;
  }

  resetSymbolModelValue();

  if (symbolFromServer) {
    symbolModelValue.types =
      GROUPING_INDUSTRY_SYMBOLS_SERVICE[Number(item.code)].types ?? '';
    symbolModelValue.sectorCodes = item.code ?? '';
  } else {
    symbolModelValue.types = item.types ?? '';
    symbolModelValue.sectorCodes = item.sectorCodes ?? '';
  }

  symbolModelValue.name = item.shortName ?? '';
  symbolModelValue.instrumentGroupIds = item.instrumentGroupIds ?? '';
  symbolModelValue.subSectorCodes = item.subSectorCodes ?? '';
};

watch(
  [isLoadingOrRevalidatingPortfolio, isLoadingOrRevalidatingWatchlist],
  () => {
    if (!selectedWatchlistModel.value?.value) {
      if (symbols.value?.length) {
        selectPortfolio();
      } else if (
        watchList.value?.length &&
        !isLoadingOrRevalidatingPortfolio.value
      ) {
        selectedWatchlistModel.value = {
          value: PORTFOLIO_INDEX + 1,
          label: watchList.value[0].title,
        };
      }
    }
  },
  {
    immediate: true,
  }
);

watch(route, () => {
  if (route.query.portfolio === 'true') {
    tab.value = 0;
    selectPortfolio();
  }
});

watch(selectedWatchlistModel, (newVal) => {
  sessionStorage.setItem('watchListSelected', JSON.stringify(newVal));
});

watch(
  [tab, selectedWatchlistModel, selectedIndustryModel],
  ([newValTab, newValSelectedWatchList, newSelectedIndustry]) => {
    const data = {
      tab: newValTab,
      selectedWatchListModel: newValSelectedWatchList,
      selectedIndustryModel: newSelectedIndustry,
      symbolModelValue: symbolModelValue,
    };
    sessionStorage.setItem('tabsSelectedAndItem', JSON.stringify(data));
  }
);

onMounted(() => {
  const tabsSelectedAndItem = sessionStorage.getItem('tabsSelectedAndItem');
  const data = JSON.parse(tabsSelectedAndItem as string);
  tab.value = data?.tab || 0;
  selectedIndustryModel.value = data?.selectedIndustryModel || {
    label: GROUPING_INDUSTRY_SYMBOLS.tradableFunds.symbols[1].name,
    value: GROUPING_INDUSTRY_SYMBOLS.tradableFunds.symbols[1].name,
  };
  selectedWatchlistModel.value = data?.selectedWatchListModel;
  handleSymbolGroupClick(
    data?.symbolModelValue || { ...SYMBOL_MODEL_INITIAL_STATE },
    false
  );
});

const retry = () => {
  getPortfolio();
  getWatchlistItems();
};

const symbolsStatus = computed(() => {
  if (
    watchlistItemsStatus.value === queryStatus.success ||
    isSymbolLoaded.value
  ) {
    return queryStatus.success;
  }

  if (
    (watchlistStatus.value === queryStatus.error ||
      watchlistItemsStatus.value === queryStatus.error) &&
    isErrorLoadingPortfolio.value
  ) {
    return queryStatus.error;
  }

  return queryStatus.loading;
});
</script>
